import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Skeleton,
  Tooltip,
  useDisclosure,
} from '@nextui-org/react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { evaluateRiskOfMessage } from '../../../store/assistant/assistantThunks';
import { Key, useMemo } from 'react';
import {
  FaCopy,
  FaEllipsis,
  FaPen,
  FaShare,
  FaTrafficLight,
  FaFaceSmile,
  FaHeartCrack,
} from 'react-icons/fa6';
import Icon from '../../base/Icon';
import { motion, useAnimate } from 'framer-motion';
import { AppHue, MessageRole } from '../../../types';
import MarkdownWrapper from '../../base/MarkdownWrapper';
import {
  evaluateClientMessageSentiment,
  evaluateClientMessageSuicidality,
} from '../../../store/chat/chatThunks';
import { selectSettingsAnalytics } from '../../../store/settings/settingsSlice';

const KEY_COPY_CLIP = 'copy_clip';
const KEY_COPY_EDIT = 'copy_edit';
const KEY_COPY_SEND = 'copy_send';
const KEY_EVAL_RISK = 'eval_risk';
const KEY_EVAL_SENT = 'eval_sent';
const KEY_EVAL_SUICIDALITY = 'suicidality';

export interface ChatBubbleSingleProps {
  messageId?: string;
  message: string;
  role: MessageRole;
  hue?: AppHue;
  onTransfer?: (message: string, sendImmediately?: boolean) => void;
  showTransfer?: boolean;
  showEvaluateRisk?: boolean;
  showEvaluateSentiment?: boolean;
  showEvaluateSuicidality?: boolean;
}

export default function ChatBubbleSingle({
  messageId,
  message,
  role,
  hue,
  onTransfer,
  showTransfer,
  showEvaluateRisk,
  showEvaluateSentiment,
  showEvaluateSuicidality,
}: ChatBubbleSingleProps) {
  const dispatch = useAppDispatch();
  const {
    isOpen: isSendModalOpen,
    onOpen: onSendModalOpen,
    onOpenChange: onSendModalOpenChange,
  } = useDisclosure();
  const [scope, animate] = useAnimate<HTMLDivElement>();

  const hasClipboard = useMemo(
    () => navigator.clipboard && window.isSecureContext,
    []
  );

  const animateTransfer = () => {
    animate(scope.current, {
      y: [0, -8, 2, 0],
    });
  };

  const handleEvaluateRisk = () => {
    dispatch(evaluateRiskOfMessage(message));
  };

  const settingsAnalytics = useAppSelector(selectSettingsAnalytics);
  const handleEvaluateSentiment = () => {
    if (messageId && settingsAnalytics.autoEvaluateSentiment) {
      dispatch(evaluateClientMessageSentiment(messageId));
    }
  };

  const handleEvaluateSuicidality = () => {
    if (messageId && settingsAnalytics.evaluateSuicidality) {
      dispatch(evaluateClientMessageSuicidality(messageId));
    }
  };

  const handleCopy = () => {
    if (hasClipboard) {
      navigator.clipboard.writeText(message);
    }
  };

  const handleTransfer = () => {
    if (onTransfer) {
      onTransfer(message, false);
      animateTransfer();
    }
  };

  const handleTransferAndSend = () => {
    if (onTransfer) {
      onTransfer(message, true);
      animateTransfer();
    }
  };

  const handleSendModalClose = (onClose: () => void) => () => {
    onClose();
    handleTransferAndSend();
  };

  const handleDropdownAction = (key: Key) => {
    switch (key) {
      case KEY_COPY_CLIP:
        handleCopy();
        break;
      case KEY_COPY_EDIT:
        handleTransfer();
        break;
      case KEY_COPY_SEND:
        onSendModalOpen();
        break;
      case KEY_EVAL_RISK:
        handleEvaluateRisk();
        break;
      case KEY_EVAL_SENT:
        handleEvaluateSentiment();
        break;
      case KEY_EVAL_SUICIDALITY:
        handleEvaluateSuicidality();
        break;
    }
  };

  const match = message.match(/(?<=<)\d+(\.\d+)?(?=>)/);
  const numberInBrackets = match ? match[0] : null;

  // `_Loading..._` is a placeholder so that the skeleton can be seen
  let markdownMessage = <MarkdownWrapper>{'_Loading..._'}</MarkdownWrapper>;

  if (numberInBrackets !== null) {
    markdownMessage = (
      <MarkdownWrapper>
        {message.replace(`<${numberInBrackets}>`, '') || '_Loading..._'}
      </MarkdownWrapper>
    );
  } else {
    markdownMessage = (
      <MarkdownWrapper>{message || '_Loading..._'}</MarkdownWrapper>
    );
  }

  const scoreChip = useMemo(() => {
    if (!numberInBrackets) {
      return;
    }

    const opacity = Math.min(Math.max(Number(numberInBrackets), -1), 1);
    const positiveOpacity = Math.max(0, opacity);
    const negativeOpacity = -Math.min(0, opacity);

    return (
      <Tooltip showArrow content="Confidence Score (individual resource)">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="relative px-2 py-1 rounded-full shadow-small overflow-hidden bg-neutral-100"
        >
          <div
            className="absolute w-full h-full left-0 top-0 bg-success-200"
            style={{ opacity: positiveOpacity }}
          />
          <div
            className="absolute w-full h-full left-0 top-0 bg-danger-200"
            style={{ opacity: negativeOpacity }}
          />
          <p className="relative text-xs text-center text-gray-700">
            {Number(numberInBrackets).toFixed(2)}
          </p>
        </motion.div>
      </Tooltip>
    );
  }, [numberInBrackets]);

  const dropdownSections = useMemo(() => {
    const sections: JSX.Element[] = [];

    const evalItems: JSX.Element[] = [];
    if (showEvaluateRisk) {
      evalItems.push(
        <DropdownItem
          key={KEY_EVAL_RISK}
          startContent={<Icon icon={FaTrafficLight} />}
        >
          Evaluate suicidal risk
        </DropdownItem>
      );
    }
    if (showEvaluateSentiment) {
      evalItems.push(
        <DropdownItem
          key={KEY_EVAL_SENT}
          startContent={<Icon icon={FaFaceSmile} />}
        >
          Evaluate sentiment (mood)
        </DropdownItem>
      );
    }
    if (showEvaluateSuicidality) {
      evalItems.push(
        <DropdownItem
          key={KEY_EVAL_SUICIDALITY}
          startContent={<Icon icon={FaHeartCrack} />}
        >
          Evaluate suicidality
        </DropdownItem>
      );
    }
    if (evalItems.length > 0) {
      sections.push(
        <DropdownSection key="evaluate" title="Evaluate" showDivider>
          {evalItems}
        </DropdownSection>
      );
    }

    const copyItems: JSX.Element[] = [];
    if (hasClipboard) {
      copyItems.push(
        <DropdownItem key={KEY_COPY_CLIP} startContent={<Icon icon={FaCopy} />}>
          Copy to clipboard
        </DropdownItem>
      );
    }
    if (showTransfer && onTransfer) {
      copyItems.push(
        <DropdownItem key={KEY_COPY_EDIT} startContent={<Icon icon={FaPen} />}>
          Copy and edit
        </DropdownItem>
      );
      copyItems.push(
        <DropdownItem
          key={KEY_COPY_SEND}
          startContent={<Icon icon={FaShare} />}
          color="danger"
          className="text-danger"
        >
          Copy and send
        </DropdownItem>
      );
    }
    if (copyItems.length > 0) {
      sections.push(
        <DropdownSection key="copy" title="Copy">
          {copyItems}
        </DropdownSection>
      );
    }
    return sections;
  }, [
    hasClipboard,
    showEvaluateRisk,
    showEvaluateSentiment,
    showEvaluateSuicidality,
    showTransfer,
    onTransfer,
  ]);

  const moreButton = (
    <>
      <Dropdown>
        <DropdownTrigger>
          <Button
            isIconOnly
            size="sm"
            color="primary"
            radius="full"
            variant="shadow"
          >
            <Icon icon={FaEllipsis} />
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Message actions"
          variant="flat"
          onAction={handleDropdownAction}
          emptyContent={<p className="text-xs italic">No actions.</p>}
        >
          {dropdownSections}
        </DropdownMenu>
      </Dropdown>
      <Modal
        isOpen={isSendModalOpen}
        onOpenChange={onSendModalOpenChange}
        size="3xl"
        backdrop="blur"
        scrollBehavior="outside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Copy and send this message?</ModalHeader>
              <ModalBody>{markdownMessage}</ModalBody>
              <ModalFooter>
                <ButtonGroup variant="flat">
                  <Button onPress={onClose}>Cancel</Button>
                  <Button
                    onPress={handleSendModalClose(onClose)}
                    color="danger"
                  >
                    Copy and send
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );

  return (
    <div ref={scope} className="group relative">
      <Card
        shadow="none"
        className={`${
          hue === 'assistant' &&
          'bg-gradient-to-br from-orange-100 to-red-100 border-red-200'
        } ${
          hue === 'client' &&
          'bg-gradient-to-br from-blue-100 to-purple-100 border-purple-200'
        } border-1`}
      >
        <CardBody>
          <Skeleton isLoaded={!!message} className="rounded-lg">
            {markdownMessage}
          </Skeleton>
        </CardBody>
      </Card>
      {scoreChip}
      <div
        className={`absolute bottom-2 translate-y-1/2 z-10 ${
          role === 'assistant'
            ? 'end-2 translate-x-1/2'
            : 'start-2 -translate-x-1/2'
        } transition-all scale-0 group-hover:scale-100`}
      >
        {moreButton}
      </div>
    </div>
  );
}
